/**
 * The vocabulary of a boardroom item, in one place.
 *
 * Pure and dependency-free so CRA's jest can test anything that imports it.
 */
import type { BoardroomItem, BoardroomItemKind, BoardroomItemStatus } from '../../types';

export const KIND_ORDER: BoardroomItemKind[] = [
  'IDEA',
  'COMPANY',
  'PRODUCT',
  'PAIN',
  'FEATURE',
  'PRICING',
  'SOLUTION_TYPE',
  'EVIDENCE',
  'CRITIQUE',
  'VERDICT',
  'QUESTION',
  'NOTE',
  'HUNCH',
  'OBSERVATION',
  'VIDEO',
  'PERSON',
  'EVENT',
  'ANGLE',
  'LOOKUP_SERP',
  'LOOKUP_PAGE',
  'LOOKUP_DEMAND',
  'LOOKUP_TRENDS',
  'LOOKUP_NEWS',
  'LOOKUP_SOCIAL',
  'LOOKUP_CONSOLE',
];

export const KIND_LABELS: Record<BoardroomItemKind, string> = {
  IDEA: 'Idea',
  COMPANY: 'Company',
  PRODUCT: 'Product',
  PAIN: 'Pain',
  FEATURE: 'Feature',
  PRICING: 'Pricing',
  SOLUTION_TYPE: 'Solution type',
  EVIDENCE: 'Evidence',
  CRITIQUE: 'Critique',
  VERDICT: 'Verdict',
  QUESTION: 'Question',
  NOTE: 'Note',
  // Nothing writes one any more; old rows keep a label that says what they are.
  HUNCH: 'Hunch (old)',
  OBSERVATION: 'Observation',
  VIDEO: 'Video',
  PERSON: 'Person',
  EVENT: 'Event',
  ANGLE: 'Angle',
  LOOKUP_SERP: 'Search',
  LOOKUP_PAGE: 'Read page',
  LOOKUP_DEMAND: 'Demand',
  LOOKUP_TRENDS: 'Trends',
  LOOKUP_NEWS: 'News',
  LOOKUP_SOCIAL: 'Social',
  LOOKUP_CONSOLE: 'Console search',
};

export const LOOKUP_KINDS: BoardroomItemKind[] = [
  'LOOKUP_SERP',
  'LOOKUP_PAGE',
  'LOOKUP_DEMAND',
  'LOOKUP_TRENDS',
  'LOOKUP_NEWS',
  'LOOKUP_SOCIAL',
  'LOOKUP_CONSOLE',
];

export const isLookup = (kind: BoardroomItemKind) => LOOKUP_KINDS.includes(kind);

/** Tailwind classes for the kind pill. Lookups share one muted tone: they are
 *  the room WAITING, not the room finding, and should read as plumbing. */
export function kindTone(kind: BoardroomItemKind): string {
  switch (kind) {
    case 'IDEA':
      return 'bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300';
    case 'COMPANY':
      return 'bg-teal-50 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300';
    case 'PRODUCT':
      return 'bg-sky-50 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300';
    case 'PAIN':
      return 'bg-rose-50 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300';
    case 'FEATURE':
      return 'bg-cyan-50 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300';
    case 'PRICING':
      return 'bg-orange-50 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300';
    case 'SOLUTION_TYPE':
      return 'bg-violet-50 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300';
    case 'EVIDENCE':
      return 'bg-lime-50 text-lime-700 dark:bg-lime-900/30 dark:text-lime-300';
    case 'CRITIQUE':
      return 'bg-amber-50 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300';
    case 'VERDICT':
      return 'bg-emerald-50 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300';
    case 'QUESTION':
      return 'bg-fuchsia-50 text-fuchsia-700 dark:bg-fuchsia-900/30 dark:text-fuchsia-300';
    case 'HUNCH':
      return 'bg-yellow-50 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300';
    case 'NOTE':
      return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200';
    case 'OBSERVATION':
      return 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300';
    case 'VIDEO':
      return 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300';
    case 'PERSON':
      return 'bg-teal-50 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300';
    case 'EVENT':
      return 'bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300';
    case 'ANGLE':
      return 'bg-pink-50 text-pink-700 dark:bg-pink-900/30 dark:text-pink-300';
    default:
      return 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300';
  }
}

export const STATUS_LABELS: Record<BoardroomItemStatus, string> = {
  OPEN: 'open',
  RESERVED: 'held',
  IN_PROGRESS: 'working',
  DONE: 'done',
  FAILED: 'failed',
  KILLED: 'ruled out',
  MERGED: 'merged',
};

/** What a status means for a LOOKUP specifically — "held" by the loop is "fetching". */
export function statusLabel(kind: BoardroomItemKind, status: BoardroomItemStatus): string {
  if (isLookup(kind)) {
    if (status === 'RESERVED' || status === 'IN_PROGRESS') return 'fetching';
    if (status === 'OPEN') return 'queued';
  }
  return STATUS_LABELS[status];
}

/** The link to show for an item: its own address, else the page it was read
 *  on (worked out by the server from what it cites). Web addresses only. */
/** Where a quote was said: the one result, story or post in a search that
 *  holds it, recorded when the item was written (boardroom/quotes.said_at). */
export interface QuoteOrigin {
  url: string;
  site?: string;
  via?: string;
  title?: string;
  author?: string;
  posted?: string;
}

export function quoteOrigin(payload: Record<string, unknown> | null | undefined): QuoteOrigin | null {
  const o = payload?.origin as QuoteOrigin | undefined;
  return o && typeof o.url === 'string' && /^https?:\/\//.test(o.url) ? o : null;
}

/** "Bluesky post by @ann, Oct 24, 2026", "Daytona Beach News-Journal story (Google News)". */
export function originLabel(o: QuoteOrigin): string {
  const what = o.via === 'web search' ? 'search result' : o.via === 'social search' || o.via === 'console' ? 'post' : 'story';
  const day = o.posted ? new Date(o.posted) : null;
  const when = day && !isNaN(day.getTime()) ? day.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '';
  const news = what === 'story' && o.via && o.via !== 'news search' ? ` (${o.via})` : '';
  return [`${o.site || 'a'} ${what}${o.author ? ` by ${o.author}` : ''}${news}`, when].filter(Boolean).join(', ');
}

export function sourceUrl(item: Pick<BoardroomItem, 'payload' | 'source'>): string | null {
  const own = item.payload?.url;
  if (typeof own === 'string' && /^https?:\/\//.test(own)) return own;
  const src = item.source?.url;
  return typeof src === 'string' && /^https?:\/\//.test(src) ? src : null;
}
